import type { StateCreator } from 'zustand';
import type { SliceMutators } from '~/store/mutators';
import { type BoxRef, boxRefEquals } from '~/types/selection';

export type CropSide = 'top' | 'bottom' | 'left' | 'right';
export type PinEdgeSide = 'top' | 'bottom' | 'left' | 'right';
export type GuidelineAxis = 'v' | 'h';

export type Selection =
  | null
  | { kind: 'cropEdge'; side: CropSide }
  | { kind: 'pinBlob'; index: number }
  | { kind: 'pinPivot'; index: number }
  | { kind: 'pinEdge'; index: number; side: PinEdgeSide }
  | { kind: 'colDivider'; index: number }
  | { kind: 'rowDivider'; index: number }
  | { kind: 'guideline'; axis: GuidelineAxis; index: number };

export type DrawMode =
  | 'none'
  | 'crop'
  | 'watermark'
  | 'pickBg'
  | 'keyRegion'
  | 'erase'
  | 'pan'
  | 'selectRegion';

export type Viewport = { zoom: number; panX: number; panY: number };

export type Guidelines = { vertical: number[]; horizontal: number[] };

export const FIT_VIEWPORT: Viewport = { zoom: 1, panX: 0, panY: 0 };

export type EditorSlice = {
  editor: {
    drawMode: DrawMode;
    selectedEdge: Selection;
    selectedBoxes: BoxRef[];
    selectedBlobs: number[];
    hoveredRegionId: string | null;
    selectedRegionId: string | null;
    showRegions: boolean;
    viewport: Viewport;
    processing: boolean;
    /** Combined fit + zoom scale set by EditorStage each frame. Overlays read
     *  this to convert between stage-px and source-image-px (e.g. snap radius). */
    stageScale: number;
    /** User-placed alignment guides in source-image px. */
    guidelines: Guidelines;
    snapToGuidelines: boolean;
  };
  setDrawMode: (m: DrawMode) => void;
  setSelection: (s: Selection) => void;
  setSelectedBoxes: (boxes: BoxRef[]) => void;
  toggleBoxSelection: (box: BoxRef) => void;
  clearBoxSelection: () => void;
  setSelectedBlobs: (indices: number[]) => void;
  toggleBlobSelection: (index: number) => void;
  clearBlobSelection: () => void;
  setHoveredRegionId: (id: string | null) => void;
  setSelectedRegionId: (id: string | null) => void;
  setShowRegions: (b: boolean) => void;
  setViewport: (v: Viewport) => void;
  resetViewport: () => void;
  setProcessing: (b: boolean) => void;
  setStageScale: (scale: number) => void;
  addGuideline: (axis: GuidelineAxis, coord: number) => void;
  moveGuideline: (axis: GuidelineAxis, index: number, coord: number) => void;
  removeGuideline: (axis: GuidelineAxis, index: number) => void;
  setSnapToGuidelines: (b: boolean) => void;
  resetEditor: () => void;
};

const INITIAL_EDITOR = {
  drawMode: 'none' as DrawMode,
  selectedEdge: null as Selection,
  selectedBoxes: [] as BoxRef[],
  selectedBlobs: [] as number[],
  hoveredRegionId: null as string | null,
  selectedRegionId: null as string | null,
  showRegions: true,
  viewport: { ...FIT_VIEWPORT },
  processing: false,
  stageScale: 1,
  guidelines: { vertical: [], horizontal: [] } as Guidelines,
  snapToGuidelines: true,
} satisfies EditorSlice['editor'];

export const createEditorSlice: StateCreator<EditorSlice, SliceMutators, [], EditorSlice> = (
  set,
) => ({
  editor: INITIAL_EDITOR,
  setDrawMode: (m) =>
    set((s) => {
      s.editor.drawMode = m;
    }),
  setSelection: (sel) =>
    set((s) => {
      s.editor.selectedEdge = sel;
      if (sel != null) {
        s.editor.selectedBoxes = [];
        s.editor.selectedBlobs = [];
      }
    }),
  setSelectedBoxes: (boxes) =>
    set((s) => {
      const seen: BoxRef[] = [];
      for (const b of boxes) {
        if (!seen.some((x) => boxRefEquals(x, b))) seen.push(b);
      }
      s.editor.selectedBoxes = seen;
      if (seen.length > 0) {
        s.editor.selectedEdge = null;
        s.editor.selectedBlobs = [];
      }
    }),
  toggleBoxSelection: (box) =>
    set((s) => {
      const idx = s.editor.selectedBoxes.findIndex((x) => boxRefEquals(x, box));
      if (idx >= 0) s.editor.selectedBoxes.splice(idx, 1);
      else s.editor.selectedBoxes.push(box);
      if (s.editor.selectedBoxes.length > 0) {
        s.editor.selectedEdge = null;
        s.editor.selectedBlobs = [];
      }
    }),
  clearBoxSelection: () =>
    set((s) => {
      s.editor.selectedBoxes = [];
    }),
  setSelectedBlobs: (indices) =>
    set((s) => {
      const seen: number[] = [];
      for (const i of indices) {
        if (!seen.includes(i)) seen.push(i);
      }
      s.editor.selectedBlobs = seen;
      if (seen.length > 0) {
        s.editor.selectedEdge = null;
        s.editor.selectedBoxes = [];
      }
    }),
  toggleBlobSelection: (index) =>
    set((s) => {
      const idx = s.editor.selectedBlobs.indexOf(index);
      if (idx >= 0) s.editor.selectedBlobs.splice(idx, 1);
      else s.editor.selectedBlobs.push(index);
      if (s.editor.selectedBlobs.length > 0) {
        s.editor.selectedEdge = null;
        s.editor.selectedBoxes = [];
      }
    }),
  clearBlobSelection: () =>
    set((s) => {
      s.editor.selectedBlobs = [];
    }),
  setHoveredRegionId: (id) =>
    set((s) => {
      if (s.editor.hoveredRegionId === id) return;
      s.editor.hoveredRegionId = id;
    }),
  setSelectedRegionId: (id) =>
    set((s) => {
      if (s.editor.selectedRegionId === id) return;
      s.editor.selectedRegionId = id;
    }),
  setShowRegions: (b) =>
    set((s) => {
      if (s.editor.showRegions === b) return;
      s.editor.showRegions = b;
    }),
  setViewport: (v) =>
    set((s) => {
      const cur = s.editor.viewport;
      if (cur.zoom === v.zoom && cur.panX === v.panX && cur.panY === v.panY) return;
      s.editor.viewport = v;
    }),
  resetViewport: () =>
    set((s) => {
      s.editor.viewport = { ...FIT_VIEWPORT };
    }),
  setProcessing: (b) =>
    set((s) => {
      if (s.editor.processing === b) return;
      s.editor.processing = b;
    }),
  setStageScale: (scale) =>
    set((s) => {
      const safe = Number.isFinite(scale) && scale > 0 ? scale : 1;
      if (s.editor.stageScale === safe) return;
      s.editor.stageScale = safe;
    }),
  addGuideline: (axis, coord) =>
    set((s) => {
      const c = Math.round(coord);
      const list = axis === 'v' ? s.editor.guidelines.vertical : s.editor.guidelines.horizontal;
      list.push(c);
      s.editor.selectedEdge = { kind: 'guideline', axis, index: list.length - 1 };
    }),
  moveGuideline: (axis, index, coord) =>
    set((s) => {
      const list = axis === 'v' ? s.editor.guidelines.vertical : s.editor.guidelines.horizontal;
      if (index < 0 || index >= list.length) return;
      const c = Math.round(coord);
      if (list[index] === c) return;
      list[index] = c;
    }),
  removeGuideline: (axis, index) =>
    set((s) => {
      const list = axis === 'v' ? s.editor.guidelines.vertical : s.editor.guidelines.horizontal;
      if (index < 0 || index >= list.length) return;
      list.splice(index, 1);
      const sel = s.editor.selectedEdge;
      if (sel?.kind === 'guideline' && sel.axis === axis && sel.index === index) {
        s.editor.selectedEdge = null;
      }
    }),
  setSnapToGuidelines: (b) =>
    set((s) => {
      if (s.editor.snapToGuidelines === b) return;
      s.editor.snapToGuidelines = b;
    }),
  resetEditor: () =>
    set((s) => {
      s.editor = { ...INITIAL_EDITOR };
    }),
});
